import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Alert, Box, Button, Divider, FormHelperText, InputAdornment, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import PageHeader from '../../../components/page-header';
import { useCustomPageCreateEditController } from './context/custom-page-create-edit-controller';
import { useCustomPageCreateEditState } from './context/custom-page-create-edit-context';
import { ImageUploadField } from '../components/image-upload-field';
import { StatusChips } from '../components/status-chips';
import { CustomPageSectionList } from './components/custom-page-section-list';
import { SectionsHeader } from '../components/sections-header';
import { CustomPageSectionDrawer } from './components/custom-page-section-drawer';
import { CustomPageSectionItemDrawer } from './components/custom-page-section-item-drawer';
import { CustomPageSeoFields } from './components/custom-page-seo-fields';
import { formatDateTime } from '../../../utils/date';

function SectionTitle({ title, description, first }: { title: string; description?: string; first?: boolean }) {
    return (
        <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: description ? 0.5 : 2, mt: first ? 0 : 2, color: 'primary.main', fontWeight: 600 }}>{title}</Typography>
            {description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>}
            <Divider sx={{ mb: 2 }} />
        </Grid>
    );
}

export function CustomPageCreateEditComponent() {
    const ctrl = useCustomPageCreateEditController();
    const state = useCustomPageCreateEditState();
    const { pageRequest, showValidationErrors, page } = state;
    const isEdit = !!state.customPageId;

    const publishAction = isEdit && page && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StatusChips isPublished={page.isPublished} />
            {page.isPublished && page.publishedAt && (
                <Typography variant="caption" color="text.secondary">em {formatDateTime(page.publishedAt)}</Typography>
            )}
            <LoadingButton
                variant={page.isPublished ? 'outlined' : 'contained'}
                color={page.isPublished ? 'warning' : 'success'}
                startIcon={page.isPublished ? <UnpublishedIcon /> : <PublishIcon />}
                onClick={ctrl.handleTogglePublication}
                loading={ctrl.isSaving}
            >
                {page.isPublished ? 'Despublicar' : 'Publicar'}
            </LoadingButton>
        </Box>
    );

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={isEdit ? (page?.title || 'Editar Página') : 'Nova Página'}
                    subtitle={isEdit ? 'Edite os dados e o conteúdo da página' : 'Informe os dados da página; as seções são adicionadas em seguida'}
                    breadcrumbs={[{ label: 'Páginas Customizadas', href: '/paginas' }, { label: isEdit ? 'Editar' : 'Nova' }]}
                    actions={publishAction}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Grid container spacing={3}>
                        <SectionTitle title="Dados da página" first />

                        <Grid item xs={12} md={isEdit ? 7 : 12}>
                            <InputLabel required>Título</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="title"
                                value={pageRequest.title || ''}
                                onChange={ctrl.handleOnChange}
                                error={showValidationErrors && !pageRequest.title?.trim()}
                            />
                            {showValidationErrors && !pageRequest.title?.trim()
                                ? <FormHelperText error>Título é obrigatório</FormHelperText>
                                : !isEdit && <FormHelperText>O endereço da página é gerado automaticamente a partir do título</FormHelperText>}
                        </Grid>
                        {isEdit && (
                            <Grid item xs={12} md={5}>
                                <InputLabel>Endereço</InputLabel>
                                <OutlinedInput
                                    fullWidth
                                    readOnly
                                    value={page?.slug || ''}
                                    startAdornment={<InputAdornment position="start">/</InputAdornment>}
                                    sx={{ bgcolor: 'action.hover' }}
                                />
                                <FormHelperText>Gerado pelo sistema na criação da página</FormHelperText>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <InputLabel>Resumo</InputLabel>
                            <OutlinedInput fullWidth multiline minRows={2} name="summary" value={pageRequest.summary || ''} onChange={ctrl.handleOnChange} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel>Imagem de capa</InputLabel>
                            <ImageUploadField
                                id="custom-page-cover"
                                value={pageRequest.coverImage}
                                onChange={ctrl.handleCoverChange}
                                onUploadingChange={ctrl.setIsUploadingCover}
                            />
                        </Grid>

                        <SectionTitle
                            title="SEO — Buscadores"
                            description="Como a página aparece no Google e em outros buscadores. Opcional, mas ajuda o cidadão a encontrar a página."
                        />
                        <CustomPageSeoFields
                            title={pageRequest.title}
                            slug={page?.slug}
                            summary={pageRequest.summary}
                            metaDescription={pageRequest.metaDescription}
                            metaKeywords={pageRequest.metaKeywords}
                            onChange={ctrl.handleOnChange}
                        />

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: '1 1 auto' }} />
                                <Button variant="outlined" startIcon={<CancelIcon />} onClick={ctrl.handleCancel} sx={{ mr: 1 }}>
                                    {isEdit ? 'Voltar' : 'Cancelar'}
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={ctrl.handleSave}
                                    loading={ctrl.isSaving}
                                    disabled={ctrl.isSaving || ctrl.isUploadingCover}
                                >
                                    Salvar
                                </LoadingButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                    {isEdit ? (
                        <React.Fragment>
                            <SectionsHeader
                                count={state.sections.length}
                                action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => ctrl.openSectionDrawer()}>Nova seção</Button>}
                            />
                            <CustomPageSectionList
                                sections={state.sections}
                                onAddSection={() => ctrl.openSectionDrawer()}
                                onEditSection={ctrl.openSectionDrawer}
                                onDeleteSection={ctrl.handleDeleteSection}
                                onToggleSectionVisibility={ctrl.handleToggleSectionVisibility}
                                onReorderSections={ctrl.handleReorderSections}
                                onAddItem={(section) => ctrl.openItemDrawer(section)}
                                onEditItem={ctrl.openItemDrawer}
                                onDeleteItem={ctrl.handleDeleteItem}
                                onReorderItems={ctrl.handleReorderItems}
                            />
                        </React.Fragment>
                    ) : (
                        <Alert severity="info">Salve a página para começar a adicionar seções de conteúdo.</Alert>
                    )}
                </Paper>
            </Grid>

            <CustomPageSectionDrawer
                open={state.sectionDrawer.open}
                section={state.sectionDrawer.section}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeSectionDrawer}
                onSave={ctrl.handleSaveSection}
            />
            <CustomPageSectionItemDrawer
                open={state.itemDrawer.open}
                section={state.itemDrawer.section}
                item={state.itemDrawer.item}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeItemDrawer}
                onSave={ctrl.handleSaveItem}
            />
        </React.Fragment>
    );
}
